import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import React from 'react';

interface DrawerStepProps {
  number: 1 | 2 | 3;
  title: string;
  isValid: boolean;
  isLocked?: boolean;
  children: React.ReactNode;
}

/**
 * DrawerStep - Step container for the capability drawer workflow
 *
 * Renders a numbered step with validation state indicator.
 * Shows checkmark when step is valid, lock icon when locked.
 */
export function DrawerStep({
  number,
  title,
  isValid,
  isLocked = false,
  children,
}: DrawerStepProps) {
  const headingId = `drawer-step-heading-${number}`;
  const statusText = isLocked ? 'Locked' : isValid ? 'Complete' : 'Incomplete';

  return (
    <section
      role="region"
      aria-labelledby={headingId}
      aria-describedby={`drawer-step-status-${number}`}
      className="border-chariot-stroke rounded-lg border"
      data-testid={`drawer-step-${number}`}
    >
      {/* Step Header */}
      <div className="flex items-center justify-between p-3 sm:p-4">
        <h3
          id={headingId}
          className="text-chariot-text-primary text-sm font-semibold tracking-wide uppercase"
        >
          {number}. {title.toUpperCase()}
        </h3>
        <div className="flex items-center gap-2">
          {isValid && !isLocked && (
            <CheckCircleIcon
              className="size-5 text-green-500"
              data-testid="step-valid-icon"
              aria-hidden="true"
            />
          )}
          {isLocked && (
            <LockClosedIcon
              className="text-chariot-text-tertiary size-5"
              data-testid="step-locked-icon"
              aria-hidden="true"
            />
          )}
          <span id={`drawer-step-status-${number}`} className="sr-only">
            Step {number}: {statusText}
          </span>
        </div>
      </div>

      {/* Step Content */}
      <div className="border-chariot-stroke border-t px-3 pt-3 pb-3 sm:px-4 sm:pt-4 sm:pb-4">
        {isLocked ? (
          <p className="text-chariot-text-tertiary py-4 text-center text-sm">
            Complete previous steps to unlock
          </p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
